import * as z from 'zod';

import { RewardTypes } from './loyaltyProgram.data';

const optionalNumberFromInput = () =>
  z.preprocess(
    val => (val === '' || val === undefined ? undefined : Number(val)),
    z.number().optional()
  );

const numberArrayFromInput = () =>
  z.preprocess(val => {
    if (!val || val.length === 0) return undefined;

    if (Array.isArray(val)) {
      return val.map(v => Number(v));
    }

    return [Number(val)];
  }, z.array(z.number()).optional());

const dateFromInput = z.preprocess(
  val => (val ? new Date(val) : undefined),
  z.date({
    required_error: 'Required',
    invalid_type_error: 'Invalid date',
  })
);

const periodSchema = z.union([z.literal(62), z.literal(63)]);

export const goalSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),

    step: z.number(),

    amount_goal: z.preprocess(
      val => (val === '' || val === undefined ? 0 : Number(val)),
      z.number().min(1, 'Sumos tikslas turi būti didesnis nei 0')
    ),

    type: z.union([z.literal(101), z.literal(102)]),

    service_type: optionalNumberFromInput(),

    reward_type: z.union([
      z.literal(RewardTypes.VIRTUAL),
      z.literal(RewardTypes.DISCOUNT),
      z.literal(RewardTypes.MEMBERSHIP),
    ]),

    reward_service_types: numberArrayFromInput(),

    discount_percent: z
      .preprocess(
        val => (val === '' || val === undefined ? undefined : Number(val)),
        z
          .number()
          .min(0)
          .max(100)
      )
      .optional(),

    wallet_reward: optionalNumberFromInput(),

    membership_id: optionalNumberFromInput(),

    discount_valid_days: optionalNumberFromInput(),
  })
  .superRefine((data, ctx) => {
    /* ----- TYPE = SERVICE ----- */

    if (data.type === 102 && !data.service_type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['service_type'],
        message: 'Pasirinkite paslaugą',
      });
    }

    /* ----- DISCOUNT REWARD ----- */

    if (data.reward_type === RewardTypes.DISCOUNT) {
      if (!data.reward_service_types) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reward_service_types'],
          message: 'Pasirinkite paslaugą',
        });
      }

      if (data.discount_percent === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['discount_percent'],
          message: 'Įveskite nuolaidą',
        });
      }
    }

    /* ----- VIRTUAL REWARD ----- */

    if (data.reward_type === RewardTypes.VIRTUAL) {
      if (data.wallet_reward === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wallet_reward'],
          message: 'Įveskite sumą',
        });
      }
    }

    /* ----- MEMBERSHIP REWARD ----- */

    if (data.reward_type === RewardTypes.MEMBERSHIP) {
      if (!data.membership_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['membership_id'],
          message: 'Pasirinkite planą',
        });
      }

      if (data.discount_valid_days === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['discount_valid_days'],
          message: 'Įveskite dienų skaičių',
        });
      }
    }
  });

export const loyaltyProgramSchema = z
  .object({
    name: z.string().min(1, 'Pavadinimas privalomas'),

    valid_from: dateFromInput,

    valid_to: dateFromInput,

    period: periodSchema,

    goals: z.array(goalSchema).min(1, 'Bent vienas tikslas privalomas'),

    description_lt: z.string().optional(),

    description_en: z.string().optional(),

    description_ru: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.valid_from && data.valid_to && data.valid_to <= data.valid_from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valid_to'],
        message: '"Galioja iki" turi būti vėliau nei "Galioja nuo"',
      });
    }
  });
